import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import axiosConn from "config/axiosConn";
import { useSortableRows, SortTh } from "./useSortableRows";
import "./ActivityReports.css";
import "./ActivityReportSchools.css";

// Indian number formatting (same as ActivityReports).
function inr(n) {
  n = Math.round(+n || 0);
  const neg = n < 0;
  let s = String(Math.abs(n));
  if (s.length <= 3) return (neg ? "-" : "") + s;
  let head = s.slice(0, -3), tail = s.slice(-3), parts = [];
  while (head.length > 2) { parts.unshift(head.slice(-2)); head = head.slice(0, -2); }
  if (head) parts.unshift(head);
  return (neg ? "-" : "") + parts.join(",") + "," + tail;
}

const REPORT_LABEL = { plantation: "Plantation", other: "Other Themes", pledge: "Pledge" };
const PAGE_SIZE = 50;
const safeName = (s) => String(s || "").replace(/[^a-z0-9]+/gi, "_").replace(/^_|_$/g, "");

export default function ActivityReportSchools() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const state = params.get("state") || "";
  const district = params.get("district") || "";
  const reportType = (params.get("reportType") || "plantation").toLowerCase();
  const pledgeMode = params.get("pledgeMode") || "after5june";
  const fromDate = params.get("from") || "";
  const toDate = params.get("to") || "";
  const isPlant = reportType === "plantation";
  // Inside a district the list can be viewed school-wise (default) or block-wise
  // (`view=blocks`); a chosen block (`block=<name>`, "__none__" = block not
  // recorded) narrows the school list to that block.
  const block = params.get("block") || "";
  const view = params.get("view") || "";
  const mode = !district ? "districts" : block ? "schools" : view === "blocks" ? "blocks" : "schools";
  const blockLabel = block === "__none__" ? "Block not recorded" : block;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  // Click-to-sort. Starts on the server's order; the school list is server-paged,
  // so sorting there orders the visible page (the Excel export has the full set).
  const { sorted: sortedRows, sort, toggle } = useSortableRows(rows);
  const [downloading, setDownloading] = useState(false);
  const [unrecorded, setUnrecorded] = useState(0);

  const bodyBase = useCallback(() => {
    const b = { state, reportType, pledgeMode };
    if (fromDate) b.fromDate = fromDate;
    if (toDate) b.toDate = toDate;
    return b;
  }, [state, reportType, pledgeMode, fromDate, toDate]);

  const fetchSchools = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosConn("post", "activity-reports/schools", {
        ...bodyBase(), district, page, limit: PAGE_SIZE, search: appliedSearch, ...(block ? { block } : {}),
      });
      if (res?.data?.success) {
        setRows(res.data.schools || []);
        setTotal(res.data.total || 0);
        setTotals(res.data.totals || null);
      } else setError("Failed to load schools.");
    } catch (e) {
      setError(e?.response?.data?.error || "Network error — please try again.");
    } finally {
      setLoading(false);
    }
  }, [bodyBase, district, block, page, appliedSearch]);

  // Block-wise: one row per block of the district (the server groups the same
  // school rows, so blocks and schools always add up to each other).
  const fetchBlocks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosConn("post", "activity-reports/schools", {
        ...bodyBase(), district, groupBy: "block", search: appliedSearch,
      });
      if (res?.data?.success) {
        setRows(res.data.blocks || []);
        setTotal(res.data.total || 0); // schools behind these blocks
        setTotals(res.data.totals || null);
        setUnrecorded(res.data.unrecorded || 0);
      } else setError("Failed to load blocks.");
    } catch (e) {
      setError(e?.response?.data?.error || "Network error — please try again.");
    } finally {
      setLoading(false);
    }
  }, [bodyBase, district, appliedSearch]);

  const fetchDistricts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosConn("post", "activity-reports/state", { ...bodyBase() });
      if (res?.data?.success) {
        setRows(res.data.reports?.[reportType]?.rows || []);
        setTotal((res.data.reports?.[reportType]?.rows || []).length);
        setTotals(res.data.reports?.[reportType]?.grand || null);
      } else setError("Failed to load districts.");
    } catch (e) {
      setError(e?.response?.data?.error || "Network error — please try again.");
    } finally {
      setLoading(false);
    }
  }, [bodyBase, reportType]);

  useEffect(() => {
    if (!state) { setError("No state in the link."); setLoading(false); return; }
    if (mode === "schools") fetchSchools();
    else if (mode === "blocks") fetchBlocks();
    else fetchDistricts();
  }, [mode, fetchSchools, fetchBlocks, fetchDistricts, state]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const applySearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setAppliedSearch(search.trim());
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await axiosConn("post", "activity-reports/schools", {
        ...bodyBase(), district, all: true, search: appliedSearch, ...(block ? { block } : {}),
      });
      const all = res?.data?.success ? res.data.schools || [] : [];
      const data = all.map((r, i) => {
        const row = {
          "#": i + 1,
          School: r.school_name,
          UDISE: r.udise_id,
          Block: r.block || "—",
          Students: r.students,
          Teachers: r.teachers,
          "Total Participants": r.participants,
        };
        if (isPlant) row["Trees Planted"] = r.trees;
        return row;
      });
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Schools");
      XLSX.writeFile(wb, `Schools_${safeName(state)}_${safeName(district)}${block ? `_${safeName(blockLabel)}` : ""}_${safeName(reportType)}.xlsx`);
    } catch (e) {
      alert("Could not generate the Excel file. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Blocks view: sheet 1 = block summary, sheet 2 = every school of the district
  // with its block, so the two can be cross-checked.
  const downloadBlocksExcel = async () => {
    setDownloading(true);
    try {
      const res = await axiosConn("post", "activity-reports/schools", { ...bodyBase(), district, all: true });
      const all = res?.data?.success ? res.data.schools || [] : [];
      if (!all.length) { alert("No schools to export."); return; }
      const blockRows = sortedRows.map((b, i) => {
        const row = { "#": i + 1, Block: b.label, Schools: b.schools, Students: b.students, Teachers: b.teachers, "Total Participants": b.participants };
        if (isPlant) row["Trees Planted"] = b.trees;
        return row;
      });
      const schoolRows = all.map((r, i) => {
        const row = { "#": i + 1, Block: r.block || "Block not recorded", School: r.school_name, UDISE: r.udise_id, Students: r.students, Teachers: r.teachers, "Total Participants": r.participants };
        if (isPlant) row["Trees Planted"] = r.trees;
        return row;
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(blockRows), "Blocks");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(schoolRows), "Schools");
      XLSX.writeFile(wb, `Blocks_${safeName(state)}_${safeName(district)}_${safeName(reportType)}.xlsx`);
    } catch (e) {
      alert("Could not generate the Excel file. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Districts view: download EVERY school of the state in one sheet (District column
  // per row) — the server returns the whole state when no district is sent.
  const downloadAllSchoolsExcel = async () => {
    setDownloading(true);
    try {
      const res = await axiosConn("post", "activity-reports/schools", { ...bodyBase(), all: true });
      const all = res?.data?.success ? res.data.schools || [] : [];
      if (!all.length) { alert("No schools to export."); return; }
      const data = all.map((r, i) => {
        const row = {
          "#": i + 1,
          District: r.district || "—",
          School: r.school_name,
          UDISE: r.udise_id,
          Block: r.block || "—",
          Students: r.students,
          Teachers: r.teachers,
          "Total Participants": r.participants,
        };
        if (isPlant) row["Trees Planted"] = r.trees;
        return row;
      });
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Schools");
      XLSX.writeFile(wb, `Schools_${safeName(state)}_ALL_DISTRICTS_${safeName(reportType)}.xlsx`);
    } catch (e) {
      alert("Could not generate the Excel file. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const schoolsHref = (districtLabel, extra = {}) =>
    `/admin/activity-report-schools?state=${encodeURIComponent(state)}&district=${encodeURIComponent(districtLabel)}&reportType=${reportType}&pledgeMode=${pledgeMode}` +
    (fromDate ? `&from=${fromDate}` : "") + (toDate ? `&to=${toDate}` : "") +
    (extra.view ? `&view=${extra.view}` : "") + (extra.block ? `&block=${encodeURIComponent(extra.block)}` : "");
  // Hard same-tab navigation (window.location) rather than SPA navigate — robust on
  // setups where a page-translation extension turns SPA route changes into a reload.
  const goToSchools = (districtLabel, extra) => { window.location.href = schoolsHref(districtLabel, extra); };
  const goToBlock = (b) => goToSchools(district, { block: b.key });

  const startIdx = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIdx = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="ars-page">
      <div className="ars-head">
        <button className="ars-back" onClick={() => navigate(-1)}>← Back</button>
        <div className="ars-titles">
          <h1 className="ars-title">
            {mode === "blocks" ? `🧭 Blocks · ${district}` : mode === "schools" ? `🏫 Schools · ${block ? `${blockLabel} · ` : ""}${district}` : `🏛️ Districts · ${state}`}
          </h1>
          <p className="ars-sub">
            {state} · {REPORT_LABEL[reportType] || reportType}
            {mode === "schools" && total > 0 && ` · ${inr(total)} schools`}
            {mode === "blocks" && !loading && ` · ${inr(rows.length)} blocks · ${inr(total)} schools`}
          </p>
        </div>
        {district && (
          <div className="ars-seg" role="tablist" aria-label="View">
            <button type="button" className={mode === "schools" && !block ? "is-on" : ""} onClick={() => goToSchools(district)}>School-wise</button>
            <button type="button" className={mode === "blocks" ? "is-on" : ""} onClick={() => goToSchools(district, { view: "blocks" })}>Block-wise</button>
          </div>
        )}
        {mode === "blocks" ? (
          <button className="ars-download" onClick={downloadBlocksExcel} disabled={downloading || loading || rows.length === 0} title="Block summary + every school of the district with its block">
            {downloading ? "Preparing…" : "⬇️ Blocks Excel"}
          </button>
        ) : mode === "schools" ? (
          <button className="ars-download" onClick={downloadExcel} disabled={downloading || loading || total === 0}>
            {downloading ? "Preparing…" : "⬇️ Download Excel"}
          </button>
        ) : (
          <button
            className="ars-download"
            onClick={downloadAllSchoolsExcel}
            disabled={downloading || loading || rows.length === 0}
            title="Download EVERY school of this state (all districts, school-wise) as one Excel"
          >
            {downloading ? "Preparing…" : "🏫 All Schools Excel"}
          </button>
        )}
      </div>

      {mode === "schools" && block && (
        <p className="ars-crumb">
          <a href={schoolsHref(district, { view: "blocks" })}>← All blocks of {district}</a>
          {" · showing schools of "}<b>{blockLabel}</b>
        </p>
      )}
      {mode === "blocks" && unrecorded > 0 && !loading && (
        <p className="ars-crumb ars-crumb--note">
          {`${inr(unrecorded)} school${unrecorded === 1 ? "" : "s"} have no block recorded yet — their blocks are being fetched from UDISE+ in the background and will appear on the next visit.`}
        </p>
      )}
      {(mode === "schools" || mode === "blocks") && (
        <form className="ars-search" onSubmit={applySearch}>
          <input
            placeholder={mode === "blocks" ? "🔍 Search block name…" : "🔍 Search school name, UDISE or block…"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit">Search</button>
          {appliedSearch && (
            <button type="button" className="ars-clear" onClick={() => { setSearch(""); setAppliedSearch(""); setPage(1); }}>Clear</button>
          )}
        </form>
      )}

      {loading ? (
        <div className="ars-state"><div className="ar-spinner" /> Loading…</div>
      ) : error ? (
        <div className="ars-state ars-error">⚠️ {error}</div>
      ) : (
        <>
          <div className="ar-table-wrap ars-table-wrap">
            <table className="ar-table ars-table">
              <thead>
                <tr>
                  <th className="ar-num">#</th>
                  <SortTh
                    label={mode === "schools" ? "School" : mode === "blocks" ? "Block" : "District"}
                    sortKey={mode === "schools" ? "school_name" : "label"}
                    sort={sort} onSort={toggle} className="ar-col-name" numeric={false}
                  />
                  {mode === "schools" && <SortTh label="UDISE" sortKey="udise_id" sort={sort} onSort={toggle} className="ar-num" />}
                  {mode === "schools" && <SortTh label="Block" sortKey="block" sort={sort} onSort={toggle} className="ars-block" numeric={false} />}
                  {mode !== "schools" && <SortTh label="Schools" sortKey="schools" sort={sort} onSort={toggle} className="ar-num" />}
                  <SortTh label="Students" sortKey="students" sort={sort} onSort={toggle} className="ar-num" />
                  <SortTh label="Teachers" sortKey="teachers" sort={sort} onSort={toggle} className="ar-num" />
                  <SortTh label="Total Participants" sortKey="participants" sort={sort} onSort={toggle} className="ar-num" />
                  {isPlant && <SortTh label="Trees Planted" sortKey="trees" sort={sort} onSort={toggle} className="ar-num" />}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={9} className="ar-empty">No records found.</td></tr>
                ) : mode === "schools" ? (
                  sortedRows.map((s, i) => (
                    <tr key={(s.udise_id || s.school_name) + i}>
                      <td className="ar-num">{(page - 1) * PAGE_SIZE + i + 1}</td>
                      <td className="ars-school">{s.school_name || "—"}</td>
                      <td className="ar-num">{s.udise_id || "—"}</td>
                      <td className="ars-block">{s.block || "—"}</td>
                      <td className="ar-num">{inr(s.students)}</td>
                      <td className="ar-num">{inr(s.teachers)}</td>
                      <td className="ar-num ar-strong">{inr(s.participants)}</td>
                      {isPlant && <td className="ar-num">{inr(s.trees)}</td>}
                    </tr>
                  ))
                ) : mode === "blocks" ? (
                  sortedRows.map((b, i) => (
                    <tr key={b.key + i} className="ars-clickable" onClick={() => goToBlock(b)} title={`View schools in ${b.label}`}>
                      <td className="ar-num">{i + 1}</td>
                      <td className="ars-school ar-label-link">
                        <a href={schoolsHref(district, { block: b.key })} onClick={(e) => e.stopPropagation()} style={{ color: "inherit", textDecoration: "underline" }}>
                          {b.label}
                        </a>
                      </td>
                      <td className="ar-num">{inr(b.schools)}</td>
                      <td className="ar-num">{inr(b.students)}</td>
                      <td className="ar-num">{inr(b.teachers)}</td>
                      <td className="ar-num ar-strong">{inr(b.participants)}</td>
                      {isPlant && <td className="ar-num">{inr(b.trees)}</td>}
                    </tr>
                  ))
                ) : (
                  sortedRows.map((d, i) => (
                    <tr key={d.label + i} className="ars-clickable" onClick={() => goToSchools(d.label)} title={`View schools in ${d.label}`}>
                      <td className="ar-num">{i + 1}</td>
                      <td className="ars-school ar-label-link">
                        <a href={schoolsHref(d.label)} onClick={(e) => e.stopPropagation()} style={{ color: "inherit", textDecoration: "underline" }}>
                          {d.label}
                        </a>
                      </td>
                      <td className="ar-num">{inr(d.schools)}</td>
                      <td className="ar-num">{inr(d.students)}</td>
                      <td className="ar-num">{inr(d.teachers)}</td>
                      <td className="ar-num ar-strong">{inr(d.participants)}</td>
                      {isPlant && <td className="ar-num">{inr(d.trees)}</td>}
                    </tr>
                  ))
                )}
              </tbody>
              {totals && rows.length > 0 && (
                <tfoot>
                  <tr className="ar-grand">
                    <td className="ar-num"></td>
                    <td className="ar-label">{mode === "schools" ? `TOTAL · ${inr(total)} schools` : mode === "blocks" ? `TOTAL · ${inr(rows.length)} blocks` : "GRAND TOTAL"}</td>
                    {mode === "schools" && <td className="ar-num"></td>}
                    {mode === "schools" && <td className="ars-block"></td>}
                    {mode === "blocks" && <td className="ar-num">{inr(total)}</td>}
                    {mode === "districts" && <td className="ar-num">{inr(totals.schools)}</td>}
                    <td className="ar-num">{inr(totals.students)}</td>
                    <td className="ar-num">{inr(totals.teachers)}</td>
                    <td className="ar-num ar-strong">{inr(totals.participants)}</td>
                    {isPlant && <td className="ar-num">{inr(totals.trees)}</td>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {mode === "schools" && total > PAGE_SIZE && (
            <div className="ars-pager">
              <button disabled={page <= 1} onClick={() => setPage(1)}>« First</button>
              <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Prev</button>
              <span>{`${inr(startIdx)}–${inr(endIdx)} of ${inr(total)}  ·  Page ${page} / ${totalPages}`}</span>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next ›</button>
              <button disabled={page >= totalPages} onClick={() => setPage(totalPages)}>Last »</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
